// lib/utils/jwt.ts
import Cookies from 'js-cookie';

export interface JWTPayload {
  id: string;
  email: string;
  role: string;
  iat: number;
  exp: number;
}

/**
 * Decode JWT token without verification (client-side only)
 */
export function decodeJWT(token: string): JWTPayload | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    
    const payload = parts[1];
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return decoded;
  } catch (error) {
    return null;
  }
}

/**
 * Check if JWT token is expired
 */
export function isTokenExpired(token: string): boolean {
  const decoded = decodeJWT(token);
  if (!decoded) return true;
  
  const currentTime = Math.floor(Date.now() / 1000);
  return decoded.exp < currentTime;
}

/**
 * Get token expiration date
 */
export function getTokenExpiration(token: string): Date | null {
  const decoded = decodeJWT(token);
  if (!decoded) return null;
  
  return new Date(decoded.exp * 1000);
}

/**
 * Set cookie with JWT expiration time
 */
export function setTokenCookie(name: string, token: string, secure: boolean = true): void {
  const decoded = decodeJWT(token);
  if (!decoded) {
    throw new Error('Invalid token');
  }
  
  const expirationDate = new Date(decoded.exp * 1000);
  Cookies.set(name, token, { 
    expires: expirationDate,
    secure,
    sameSite: 'strict'
  });
}

/**
 * Check if current session is valid
 */
export function isSessionValid(): boolean {
  const accessToken = Cookies.get('accessToken');
  if (!accessToken) return false;
  
  return !isTokenExpired(accessToken);
}

/**
 * Clear all authentication cookies
 */
export function clearAuthCookies(): void {
  Cookies.remove('accessToken');
  Cookies.remove('refreshToken');
}